/**
 * agentActivityFeed — the spine's per-section live "what is Asthra doing" feed.
 *
 * The run SSE stream now carries every AsthraAgent / tool-handler event
 * verbatim (agent lifecycle, LLM rounds, tool calls + results, retrieval
 * cascade steps, superintendent + librarian steps), each with the emitting
 * agent's span identity (``section_response.depth`` etc.). This module turns
 * one event into one human line and keeps a bounded, shell-owned ring buffer
 * per section — separate from ``SpineSection.subline`` so it survives the
 * ``section_state_changed`` subline wipe and spine rebuilds (same pattern as
 * ``liveDocLoadTracker``).
 *
 * Pure: no React, no I/O. Labels come from the tool display SSOT
 * (``config/toolDisplayNames.ts``) so the feed, the chat rail and Action
 * History never drift apart.
 */

import {
  agentLabelFromPayload,
  buildLiveStatusPhrase,
  buildLiveStatusPhraseFromQuery,
  describeSubmitAttempt,
  describeToolCallFromQuery,
  submitAttemptOf,
  withAgentLabel,
} from "../../config/toolDisplayNames";
import type { ProgressEvent } from "../../types";

export type AgentActivityKind = "tool" | "tool_done" | "retrieval" | "step" | "error";

export interface AgentActivityLine {
  /** Stable per-line key (monotonic). */
  id: string;
  /** Client wall-clock ms when the line was produced. */
  at: number;
  /** Nesting depth of the emitting agent (0 = the section's main agent). */
  depth: number;
  kind: AgentActivityKind;
  text: string;
  /** Emitting agent's label ("Drafter 2/5", "Reviewer") when the payload
   *  names one. ``text`` already carries it as a "Label · " prefix; the spine's
   *  live log shows it as a separate mono tag. */
  agent?: string;
  /** Which drafting stage this event is evidence of, when it says. */
  stage?: DraftStage;
}

/**
 * Where a section's pass stands, as far as one event can tell: reading its
 * sources (retrieval, librarian, tool reads), drafting (draft handed over /
 * saved / submitted) or reviewing (checks phase, reviewer, regulatory audit).
 */
export type DraftStage = "sources" | "drafting" | "reviewing";

const REVIEW_STAGE_EVENTS: ReadonlySet<string> = new Set([
  "review_start",
  "review_done",
  "check_start",
  "check_done",
  "audit_progress",
  "audit_guidelines_loaded",
  "audit_context_inferred",
  "audit_precedents_fetched",
]);

const DRAFT_STAGE_EVENTS: ReadonlySet<string> = new Set([
  "checklist_drafting",
  "drafting_response",
  "stage_draft_saved",
  "assemble_done",
  "terminal_accepted",
]);

const SOURCE_STAGE_EVENTS: ReadonlySet<string> = new Set([
  "retrieval_step",
  "checklist_item",
  "librarian_l1_pick",
  "librarian_tree_render",
  "librarian_l2_pick",
  "tool_call_start",
  "tool_call_result",
]);

/** The stage one event is evidence of, or null when it says nothing about it. */
export function stageOfEvent(
  eventType: string,
  payload: Record<string, unknown>,
  toolName?: string | null,
): DraftStage | null {
  if (REVIEW_STAGE_EVENTS.has(eventType)) return "reviewing";
  if (eventType === "phase_start" || eventType === "phase_done") {
    return payload.phase === "checks" ? "reviewing" : null;
  }
  if (agentLabelFromPayload(payload)?.startsWith("Reviewer")) return "reviewing";
  if (DRAFT_STAGE_EVENTS.has(eventType) || toolName === "submit_draft") return "drafting";
  if (SOURCE_STAGE_EVENTS.has(eventType)) return "sources";
  return null;
}

/** Agent label + stage tags for a line (fields omitted when absent). */
function lineTags(event: ProgressEvent, payload: Record<string, unknown>): Pick<AgentActivityLine, "agent" | "stage"> {
  const agent = agentLabelFromPayload(payload);
  const stage = stageOfEvent(event.event_type, payload, event.tool_name);
  return { ...(agent ? { agent } : {}), ...(stage ? { stage } : {}) };
}

export type AgentActivityMap = ReadonlyMap<string, readonly AgentActivityLine[]>;

/** Lines kept per section — the newest N; older ones scroll off. */
export const ACTIVITY_FEED_MAX = 6;

/** Agent/superintendent/librarian step events rendered from the server's ``message``. */
const STEP_EVENTS: ReadonlySet<string> = new Set([
  // The module's fresh reviewer (after assembly): its start and verdict.
  "review_start",
  "review_done",
  // A section's phases (first draft → checks, each with its clock and spend)
  // and every check of the checks phase starting / finishing.
  "phase_start",
  "phase_done",
  "check_start",
  "check_done",
  "agent_start",
  "agent_end",
  "round_start",
  "llm_call_start",
  "llm_call_end",
  "terminal_accepted",
  "task_start",
  "drafting_response",
  "lead_start",
  "lead_done",
  // Superintendent node agents starting / finishing — the server's message
  // carries the "Agent i/N · " prefix.
  "plan_node_start",
  "plan_node_result",
  // A section's draft saved at a stage (plan / part i/K / assembled / reviewed)
  // — Resume reuses these, so the writer sees what is already banked.
  "stage_draft_saved",
  // A node's checklist items: retrieval started / refused / verdict / settled /
  // ticked, and its move to drafting — the server's message names the items
  // and what each still lacks.
  "checklist_item",
  "checklist_drafting",
  "plan_layer_done",
  "assemble_done",
  "superintendent_complete",
  "librarian_l1_pick",
  "librarian_tree_render",
  "librarian_l2_pick",
  // Regulatory-audit narration. The auditor emits these with a pre-formatted,
  // audit-branded ``message`` ("Searching the audit library: …", "pass 2 of
  // 12", "Loaded guidance for … (9 expectations)"), so the "Show steps" feed
  // renders the audit's step-by-step trace. (The raw agent tool events don't
  // reach here with the top-level tool_name the tool branch needs — the audit's
  // on_progress carries that in section_response — so these branded lines are
  // what surface the audit steps.)
  "audit_progress",
  "audit_guidelines_loaded",
  "audit_context_inferred",
  "audit_precedents_fetched",
]);

const ERROR_EVENTS: ReadonlySet<string> = new Set([
  "resolve_span_failed",
  "api_error",
  // An agent call cut off at its max_tokens (server retries it once at 32k).
  "llm_output_truncated",
]);

/** A section's feed is over (cleared) on these. */
export const ACTIVITY_CLEAR_SECTION_EVENTS: ReadonlySet<string> = new Set([
  "section_ready",
  "regen_complete",
  "resolve_section_proposed",
  "resolve_section_complete",
  "resolve_section_failed",
  "resolve_section_skipped",
  "module_skipped",
  // Regulatory audit terminals — close the step feed the audit opened.
  "audit_complete",
  "audit_error",
  // The automatic missing-info fill finished (outcome is on the Issues tab).
  "gap_fill_complete",
]);

/** Every feed is over on these (run-level terminals). */
export const ACTIVITY_CLEAR_ALL_EVENTS: ReadonlySet<string> = new Set([
  "pipeline_complete",
  "resolve_finished",
  "error",
  "cancelled",
]);

/** A section's feed restarts on these (a new pass on the same row) — including
 *  the start of a regulatory audit, so the audit's step feed opens fresh rather
 *  than trailing the prior drafting/regen lines. */
export const ACTIVITY_RESET_SECTION_EVENTS: ReadonlySet<string> = new Set([
  "module_start",
  // A "Fix with Asthra" pass starting on the section.
  "resolve_section_started",
  "regen_started",
  "audit_started",
  // The automatic missing-info fill after the review: a fresh feed, opened
  // with its own "Filling N flags…" line.
  "gap_fill_started",
]);

/** The automatic missing-info fill's agent label in the feed. */
const GAP_FILL_LABEL = "Gap fill";

let lineSeq = 0;

const RETRIEVAL_VERB: Record<string, string> = { ok: "found", partial: "partial", fail: "no match" };

/**
 * One event → one feed line, or null for events with no writer-facing line.
 * ``now`` is injectable for tests.
 */
export function describeAgentEvent(event: ProgressEvent, now: number = Date.now()): AgentActivityLine | null {
  const payload = (event.section_response ?? {}) as Record<string, unknown>;
  const depth = typeof payload.depth === "number" && Number.isFinite(payload.depth) ? payload.depth : 0;
  const type = event.event_type;
  let kind: AgentActivityKind;
  let text: string;

  // Automatic missing-info fill. Its agent's steps arrive wrapped
  // (``section_response.step`` = the original tool_call_* / retrieval_step /
  // error type) so they never repaint the drafted row as drafting; unwrap and
  // describe each exactly like the agent's own step, labelled "Gap fill".
  if (type === "gap_fill_progress") {
    const step = typeof payload.step === "string" ? payload.step : "";
    if (step && !step.startsWith("gap_fill_")) {
      const inner = describeAgentEvent(
        { ...event, event_type: step === "error" ? "resolve_span_failed" : step } as ProgressEvent,
        now,
      );
      if (inner) return inner;
    }
    return {
      id: `${type}-${lineSeq++}`,
      at: now,
      depth,
      kind: "step",
      text: `${GAP_FILL_LABEL} · ${event.message || "filling missing-info flags…"}`,
      agent: GAP_FILL_LABEL,
    };
  }
  if (type === "gap_fill_started") {
    return {
      id: `${type}-${lineSeq++}`,
      at: now,
      depth: 0,
      kind: "step",
      text: `${GAP_FILL_LABEL} · ${event.message || "filling missing-info flags…"}`,
      agent: GAP_FILL_LABEL,
    };
  }

  if (type === "tool_call_start") {
    if (!event.tool_name) return null;
    kind = "tool";
    text = withAgentLabel(buildLiveStatusPhraseFromQuery(event.tool_name, event.tool_query), payload);
  } else if (type === "tool_call_result") {
    if (!event.tool_name) return null;
    const submit = submitAttemptOf(payload.submit);
    kind = submit?.outcome === "rejected" ? "error" : "tool_done";
    if (submit) {
      text = describeSubmitAttempt(submit);
    } else {
      text = describeToolCallFromQuery(event.tool_name, event.tool_query, true);
      if (payload.ok === false) text += " — failed";
    }
    text = withAgentLabel(text, payload);
  } else if (type === "retrieval_step") {
    kind = "retrieval";
    const tool = typeof payload.tool === "string" ? payload.tool : null;
    const hint =
      typeof payload.hint === "string"
        ? payload.hint
        : typeof payload.file_label === "string"
          ? payload.file_label
          : undefined;
    const surface = typeof payload.surface === "string" ? payload.surface : undefined;
    const status = typeof payload.status === "string" ? payload.status : "";
    if (tool) {
      // Same SSOT mapping the chat rail uses for retrieval_step.
      const phrase = buildLiveStatusPhrase(tool, hint ? { query: hint } : undefined, surface);
      const verb = RETRIEVAL_VERB[status];
      text = withAgentLabel(verb ? `${phrase} — ${verb}` : phrase, payload);
    } else {
      text = event.message || "retrieving…";
    }
    return { id: `${type}-${lineSeq++}`, at: now, depth: depth + 1, kind, text, ...lineTags(event, payload) };
  } else if (type === "note_written") {
    // A drafter saved to the shared workspace notes (decisions register, evidence, log).
    kind = "step";
    const key = typeof payload.key === "string" ? payload.key : "";
    const fallback = `${payload.mode === "append" ? "Added to" : "Saved"} workspace note ${key}`.trim();
    text = withAgentLabel(event.message || fallback, payload);
  } else if (STEP_EVENTS.has(type)) {
    kind = "step";
    text = event.message || type.replace(/_/g, " ");
  } else if (ERROR_EVENTS.has(type)) {
    kind = "error";
    text = event.message || "failed";
  } else {
    return null;
  }
  return { id: `${type}-${lineSeq++}`, at: now, depth, kind, text, ...lineTags(event, payload) };
}

/** Append a line to a section's feed, keeping the newest ``max``. Returns a new map. */
export function pushActivity(
  map: AgentActivityMap,
  sectionId: string,
  line: AgentActivityLine,
  max: number = ACTIVITY_FEED_MAX,
): AgentActivityMap {
  const next = new Map(map);
  const prev = map.get(sectionId) ?? [];
  const lines = [...prev, line];
  next.set(sectionId, lines.length > max ? lines.slice(lines.length - max) : lines);
  return next;
}

/** Drop a section's feed. Returns the same map when nothing changed. */
export function clearActivity(map: AgentActivityMap, sectionId: string): AgentActivityMap {
  if (!map.has(sectionId)) return map;
  const next = new Map(map);
  next.delete(sectionId);
  return next;
}

export function clearAllActivity(map: AgentActivityMap): AgentActivityMap {
  return map.size === 0 ? map : new Map();
}

/**
 * Fold one SSE event into the feed map: resets on a new pass for the row,
 * clears on the row's terminal, clears everything on run terminals, otherwise
 * appends the described line. Returns the same map instance when unchanged so
 * React state setters can skip a render.
 */
export function applyActivityEvent(
  map: AgentActivityMap,
  event: ProgressEvent,
  now: number = Date.now(),
): AgentActivityMap {
  const type = event.event_type;
  if (ACTIVITY_CLEAR_ALL_EVENTS.has(type)) return clearAllActivity(map);
  const sectionId = event.module_id;
  if (!sectionId) return map;
  if (ACTIVITY_RESET_SECTION_EVENTS.has(type)) {
    const cleared = clearActivity(map, sectionId);
    // A fill opens its fresh feed with its own first line.
    const opener = type === "gap_fill_started" ? describeAgentEvent(event, now) : null;
    return opener ? pushActivity(cleared, sectionId, opener) : cleared;
  }
  if (ACTIVITY_CLEAR_SECTION_EVENTS.has(type)) return clearActivity(map, sectionId);
  const line = describeAgentEvent(event, now);
  if (!line) return map;
  return pushActivity(map, sectionId, line);
}

/** The most recent line of a section's feed, if any. */
export function latestActivity(map: AgentActivityMap | undefined, sectionId: string): AgentActivityLine | null {
  const lines = map?.get(sectionId);
  return lines && lines.length > 0 ? lines[lines.length - 1] : null;
}

/** True when any section has a live feed (keeps the spine's elapsed tick running). */
export function hasAnyActivity(map: AgentActivityMap | undefined): boolean {
  return !!map && map.size > 0;
}

/**
 * The newest line across every section's feed (largest ``at``; ties go to the
 * later-pushed line). Drives the spine's single run-level "live activity"
 * strip. Null when no section has a feed.
 */
export function latestActivityAcross(map: AgentActivityMap | undefined): AgentActivityLine | null {
  if (!map) return null;
  let best: AgentActivityLine | null = null;
  for (const lines of map.values()) {
    const last = lines.length > 0 ? lines[lines.length - 1] : null;
    if (last && (!best || last.at >= best.at)) best = last;
  }
  return best;
}

/**
 * Run-level "latest activity" fold for the spine strip. Unlike the per-section
 * feed it is NOT cleared on section / run terminals, so the strip keeps the
 * run's last real line (e.g. the reviewer's verdict) after drafting ends.
 * The host resets it to null when a new run starts. Returns ``prev`` when the
 * event has no writer-facing line.
 */
export function nextRunActivity(
  prev: AgentActivityLine | null,
  event: ProgressEvent,
  now: number = Date.now(),
): AgentActivityLine | null {
  return describeAgentEvent(event, now) ?? prev;
}
